import React from "react";
import { AlertCircle, Phone, Mail, Users, ArrowRight, ArrowLeft, Loader2, CalendarDays, Download } from "lucide-react";
import { CONTACT } from "@/data/site";
import { ErrorBox, RoomCard } from "@/components/booking/parts";

export const Step2Rooms = ({ ctx }) => {
  const {
    v, form, datesValid, fmtRange, effGuests, searchLoading, search, roomsWanted,
    roomName, roomTag, roomDesc, roomImg, downloadQuote, chooseSingle, chooseCombo,
    pickAlternative, state,
  } = ctx;

  return (
    <div className="space-y-6" data-testid="step-rooms">
      <button data-testid="edit-dates-btn" onClick={() => window.history.back()} className="inline-flex items-center gap-2 text-sm text-[#1D3557] hover:underline">
        <ArrowLeft className="w-4 h-4" /> {v("editDates")}
      </button>

      <div className="flex flex-wrap items-center gap-2 text-sm text-[#5C5C5C]">
        <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#1D3557]" /> {datesValid ? fmtRange(form.arrival, form.departure) : ""}</span>
        <span className="text-black/20">·</span>
        <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-[#1D3557]" /> {effGuests} {effGuests === 1 ? v("guest") : v("guests")}</span>
      </div>

      {searchLoading ? (
        <div className="flex items-center gap-3 p-8 justify-center text-[#5C5C5C]">
          <Loader2 className="w-5 h-5 animate-spin text-[#1D3557]" /> {v("verifying")}
        </div>
      ) : search && search.can_host && search.single && search.single.length > 0 ? (
        <div className="space-y-5" data-testid="available-rooms">
          <div>
            <h2 className="font-display text-2xl text-[#1A1A1A]">{v("availTitle")}</h2>
            <p className="mt-1 text-sm text-[#5C5C5C]">{v("onlyThese")}</p>
          </div>
          {search.single.map((o) => (
            <RoomCard key={o.slug} slug={o.slug} total={o.total} nights={search.nights} currency={search.currency}
              name={roomName(o.slug)} tag={roomTag(o.slug)} desc={roomDesc(o.slug)} img={roomImg(o.slug)}
              maxGuests={o.max_guests} v={v} onChoose={() => chooseSingle(o)} testid={`choose-room-${o.slug}`}
              onQuote={() => downloadQuote(roomName(o.slug), o.total, 1, [o.slug])} quoteTestid={`quote-room-${o.slug}`} />
          ))}
        </div>
      ) : search && search.can_host && search.combos && search.combos.length > 0 ? (
        <div className="space-y-5" data-testid="available-combos">
          <div>
            <h2 className="font-display text-2xl text-[#1A1A1A]">{v("comboTitle")}</h2>
            <p className="mt-1 text-sm text-[#5C5C5C]">{v("comboHint").replace("{n}", roomsWanted)}</p>
          </div>
          {search.combos.map((c, ci) => (
            <div key={ci} data-testid={`choose-combo-${ci}`} className="p-5 rounded-2xl border border-black/10 bg-[#F2EFEB]/40">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {c.rooms.map((slug) => (
                  <span key={slug} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-black/10 text-sm text-[#1A1A1A]">
                    <img src={roomImg(slug)} alt={roomName(slug)} className="w-6 h-6 rounded-full object-cover" /> {roomName(slug)}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span className="inline-flex items-center gap-1.5 text-sm text-[#5C5C5C]"><Users className="w-4 h-4" /> {v("maxGuests").replace("{n}", c.capacity)}</span>
                <div className="flex items-center gap-3">
                  {c.total != null && <span className="font-display text-2xl text-[#1A1A1A]">€ {Math.round(c.total)}</span>}
                  <button data-testid={`quote-combo-${ci}`} onClick={() => downloadQuote(c.rooms.map(roomName).join(" + "), c.total, c.rooms.length, c.rooms)}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-[#1D3557]/30 text-[#1D3557] text-sm font-medium hover:bg-[#1D3557]/5 transition-all">
                    <Download className="w-4 h-4" /> {v("downloadQuote")}
                  </button>
                  <button data-testid={`select-combo-${ci}`} onClick={() => chooseCombo(c)} className="px-5 py-2.5 rounded-full bg-[#1D3557] text-white text-sm font-medium hover:bg-[#345B73] transition-all">{v("chooseCombo")}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-5" data-testid="no-availability">
          {search && search.min_stay_required ? (
            <div data-testid="min-stay-notice" className="flex items-start gap-3 p-5 rounded-2xl bg-[#1D3557]/8 border border-[#1D3557]/20">
              <AlertCircle className="w-5 h-5 text-[#1D3557] flex-shrink-0 mt-0.5" />
              <p className="text-[#1D3557] font-medium">{v("minStayNotice").replace("{n}", search.min_stay_required)}</p>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-5 rounded-2xl bg-[#7A2B33]/8 border border-[#7A2B33]/20">
              <AlertCircle className="w-5 h-5 text-[#7A2B33] flex-shrink-0 mt-0.5" />
              <p className="text-[#7A2B33] font-medium">{v("noAvailTitle")}</p>
            </div>
          )}
          {search && search.alternatives && search.alternatives.length > 0 ? (
            <div data-testid="alternatives">
              <p className="text-sm text-[#5C5C5C] mb-3">{v("altTitle")}</p>
              <div className="space-y-2.5">
                {search.alternatives.map((alt, ai) => {
                  const lowest = (alt.single || []).reduce((m, o) => (o.total != null && (m == null || o.total < m) ? o.total : m), null);
                  return (
                    <button key={ai} data-testid={`alt-${ai}`} onClick={() => pickAlternative(alt.arrival, alt.departure)}
                      className="w-full flex items-center justify-between gap-4 p-4 rounded-xl bg-white border border-black/10 hover:border-[#1D3557]/40 hover:bg-[#F2EFEB]/40 transition-all text-left">
                      <span className="inline-flex items-center gap-2 text-[#1A1A1A]">
                        <CalendarDays className="w-4 h-4 text-[#1D3557]" /> {fmtRange(alt.arrival, alt.departure)}
                      </span>
                      <span className="inline-flex items-center gap-3 text-sm">
                        {lowest != null && <span className="text-[#5C5C5C]">€ {Math.round(lowest)}</span>}
                        <span className="text-[#1D3557] font-medium inline-flex items-center gap-1">{v("selectDatesBtn")} <ArrowRight className="w-4 h-4" /></span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#5C5C5C]">{v("noAlt")}</p>
          )}
          <div className="flex flex-wrap gap-4 pt-2">
            <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 text-[#1D3557] hover:underline"><Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}</a>
            <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 text-[#1D3557] hover:underline break-all"><Mail className="w-4 h-4" /> {CONTACT.email}</a>
          </div>
        </div>
      )}

      {state.error && <ErrorBox msg={state.error} />}
    </div>
  );
};
